import { createSelector } from "reselect";

import type { IAppsAndroidFiltersDto, IAppsIosFiltersDto } from "app/pages/sales-intelligence/store/find-apps/types";
import { AppsSourceType } from "app/pages/sales-intelligence/store/find-apps/types/appsSourceType";
import type { AppsSearchRequestDto } from "app/pages/sales-intelligence/store/find-apps/types/dto";
import type {
    IFindAppsRequestState,
    IFindAppsState,
    IFindAppsTabState,
} from "app/pages/sales-intelligence/store/find-apps/types/state";
import { selectSalesIntelligenceState } from "app/pages/sales-intelligence/store/selectors";
import { createStatePropertySelector } from "app/pages/workspace/sales/helpers";
import type { RootState } from "single-spa/store/types";

const selectFindAppsState = createStatePropertySelector<IFindAppsState>(selectSalesIntelligenceState("findApps"));

export const selectActiveAppSourceType = selectFindAppsState("activeAppSourceType");
export const selectIsRowSelectionEnabled = selectFindAppsState("isRowSelectionEnabled");
export const selectSelectedRows = selectFindAppsState("selectedRows");
export const selectSelectedRowKeys = createSelector(selectSelectedRows, (selectedRows) =>
    selectedRows.map(({ key }) => key),
);

const selectFindAppsIosState = selectFindAppsState(AppsSourceType.AppleStore);
const selectFindAppsAndroidState = selectFindAppsState(AppsSourceType.GoogleStore);

export const selectFindAppsIosExpandedFilter = createSelector(
    selectFindAppsIosState,
    (iosState: IFindAppsTabState<IAppsIosFiltersDto>) => iosState.expandedFilter,
);
export const selectFindAppsAndroidExpandedFilter = createSelector(
    selectFindAppsAndroidState,
    (androidState: IFindAppsTabState<IAppsAndroidFiltersDto>) => androidState.expandedFilter,
);

const selectFindAppsIosRequestState = createSelector(
    selectFindAppsIosState,
    (iosState: IFindAppsTabState<IAppsIosFiltersDto>) => iosState.request,
);
const selectFindAppsAndroidRequestState = createSelector(
    selectFindAppsAndroidState,
    (androidState: IFindAppsTabState<IAppsAndroidFiltersDto>) => androidState.request,
);

export const selectFindAppsIosFiltersValue = createSelector(
    selectFindAppsIosRequestState,
    (iosState: IFindAppsRequestState<IAppsIosFiltersDto>) => iosState.filters,
);
export const selectFindAppsAndroidFiltersValue = createSelector(
    selectFindAppsAndroidRequestState,
    (androidState: IFindAppsRequestState<IAppsAndroidFiltersDto>) => androidState.filters,
);

const selectIosSearchText = createSelector(selectFindAppsIosRequestState, ({ searchText }) => searchText);

const selectAndroidSearchText = createSelector(
    selectFindAppsAndroidRequestState,
    ({ searchText }) => searchText,
);

export const selectIosRequestDto = createSelector(
    selectFindAppsIosRequestState,
    ({ filters, searchText, page, pageSize, orderBy, asc }) => ({
        page,
        pageSize,
        orderBy,
        asc,
        filters: {
            searchText,
            ...filters,
        },
    }),
);

export const selectAndroidRequestDto = createSelector(
    selectFindAppsAndroidRequestState,
    ({ filters, searchText, page, pageSize, orderBy, asc }) => ({
        page,
        pageSize,
        orderBy,
        asc,
        filters: {
            searchText,
            ...filters,
        },
    }),
);

export const selectAppsSearchRequestDto = createSelector(
    [selectActiveAppSourceType, (state: RootState) => state],
    (appSourceType, state): AppsSearchRequestDto => {
        switch (appSourceType) {
            case AppsSourceType.AppleStore:
                return selectIosRequestDto(state);
            case AppsSourceType.GoogleStore:
                return selectAndroidRequestDto(state);
            default:
                return {} as AppsSearchRequestDto;
        }
    },
);

export const selectAppsSearchText = createSelector(
    [selectActiveAppSourceType, (state: RootState) => state],
    (appSourceType, state): string => {
        switch (appSourceType) {
            case AppsSourceType.AppleStore:
                return selectIosSearchText(state);
            case AppsSourceType.GoogleStore:
                return selectAndroidSearchText(state);
            default:
                return "";
        }
    },
);
